/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import {
  Box,
  Typography,
  Snackbar,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import Topheader from "../../../../Layout/header/header";
import Sidebar from "../sidebar/sidebar";
import { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import "./categories.css";
import Divider from "@mui/material/Divider";
import {
  DndContext,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import ListItemIcon from "@mui/material/ListItemIcon";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import Avatar from "@mui/material/Avatar";
import APIManager from "../../../../Services/ApiManager/ApiManager";
import Grid from "@mui/material/Grid2";
import Button from "@mui/material/Button";
import PopupDialog from "../../../../Components/PopupDialog";

function SortableItem({ item }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    borderBottom: "2px solid #e8e8e8",
  };

  return (
    <ListItem ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <ListItemIcon sx={{ cursor: "move" }}>
        <DragIndicatorIcon />
      </ListItemIcon>
      <ListItemAvatar>
        <Avatar alt={item.categoryName} src={item.iconUrl} />
      </ListItemAvatar>
      <ListItemText primary={item.categoryName} />
    </ListItem>
  );
}

SortableItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.number,
    categoryName: PropTypes.string,
    iconUrl: PropTypes.string,
  }),
};


export default function Categories() {
  const [componentName, setComponentName] = useState("");
  const [openErrorPopup, setOpenErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [headerMessage, setHeaderMessage] = useState("");
  const [loader, setLoader] = useState(true);
  const [portfolio, setPortfolio] = useState("");
  const [categories, setCategories] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [disableSaveButton, setDisableSaveButton] = useState(true);

  function handleDataFromChild(data) {
    setPortfolio(data);
  }

  useEffect(() => {
    if (portfolio && portfolio !== "") {
      APIManager.getOEMCategories(portfolio)
        .then((json) => {
          setLoader(false);
          if (json && json.length > 0) {
            let data = json.map((item, index) => ({
              id: index,
              categoryName: item.categoryName,
              iconUrl: item.iconUrl,
            }));
            setCategories(data);
          }
        })
        .catch((error) => {
          console.error("Error fetching data:", error);
          setComponentName("appError");
          setErrorMessage(error.message);
          setHeaderMessage(
            error.message === "jwt expired" ? "401 Token Expired" : "Error"
          );
          setOpenErrorPopup(true);
          setLoader(false);
        });
    }
  }, [portfolio]);

  const containerRef = useRef();

  const _onListChange = (newList) => {
    setCategories(newList);
    setDisableSaveButton(false);
  };

  const saveCategorySequence = () => {
    setLoader(true);
    let saveData = categories.map((item) => item.categoryName);
    APIManager.saveCategorySequence({ categoryList: saveData }, portfolio)
      .then((json) => {
        setSnackMsg(`Updated successfully.`);
        setOpenSnackbar(true);
        setDisableSaveButton(true);
        setLoader(false);
      })
      .catch((error) => {
        setSnackMsg(error?.message);
        setOpenSnackbar(true);
        setLoader(false);
      });
  };

  return (
    <>
      <Topheader />
      {loader && (
        <Backdrop
          sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
          open={loader}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      <Box
        sx={{
          marginTop: "3%",
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "-webkit-box",
          }}
        >
          <Sidebar sendDataToParent={handleDataFromChild} page="categories" />
          <Box sx={{ flexGrow: 1, marginTop: "16px", marginLeft: "20px" }}>
            <Box
              justifyContent="space-between"
              alignItems="center"
              mb={2}
              width="89%"
            >
              <Grid container spacing={2}>
                <Grid size={10.8}>
                  <Typography
                    sx={{
                      fontSize: "20px",
                      fontWeight: "bold",
                      marginTop: "2%",
                    }}
                  >
                    {portfolio} - Portfolio Settings
                  </Typography>
                </Grid>
                <Grid size={1.2}>
                  <Button
                    sx={{ marginTop: "2%" }}
                    onClick={saveCategorySequence}
                    variant="contained"
                    size="small"
                    disabled={
                      sessionStorage.getItem("readonly_OemAdmin") === "true" ||
                      disableSaveButton
                    }
                  >
                    Save
                  </Button>
                </Grid>
              </Grid>
              <Divider sx={{ marginTop: "2% !important" }} />
              <Typography
                sx={{ fontSize: "18px", fontWeight: "bold", marginTop: "2%" }}
              >
                Category Settings
              </Typography>
              <Typography
                sx={{ fontSize: "18px", fontWeight: "bold", marginTop: "2%" }}
              >
                Categorization Sequence
              </Typography>
              <Typography
                sx={{ color: "#717273", fontSize: "12px" }}
                variant="subtitle2"
                gutterBottom
              >
                Arrange your preferred categories for 3rd party apps in a
                specific sequence, and they will be displayed in the same order
                on the head unit.
              </Typography>
            </Box>

            <Box>
              {categories.length > 0 && (
                <List
                  sx={{
                    width: "60%",
                    border: "2px solid #e8e8e8",
                    bgcolor: "background.paper",
                    position: "relative",
                    overflow: "auto",
                    maxHeight: 300,
                    paddingTop: 0,
                    "& ul": { padding: 0 },
                  }}
                >
                  <ListSubheader
                    sx={{ bgcolor: "#e8e8e8", zIndex: "999" }}
                  >{`Category`}</ListSubheader>
                 <DndContext
  collisionDetection={closestCenter}
  onDragEnd={({ active, over }) => {
    if (!over) return;
    const oldIndex = categories.findIndex((c) => c.id === active.id);
    const newIndex = categories.findIndex((c) => c.id === over.id);
    if (oldIndex !== newIndex) {
      _onListChange(arrayMove(categories, oldIndex, newIndex));
    }
  }}
>
  <SortableContext
    items={categories.map((c) => c.id)}
    strategy={verticalListSortingStrategy}
  >
    <div ref={containerRef}>
      {categories.map((item) => (
        <SortableItem key={item.id} item={item} />
      ))}
    </div>
  </SortableContext>
</DndContext>

                </List>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      {loader && (
        <Backdrop
          sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
          open={loader}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        message={snackMsg}
      />
      <PopupDialog
        openErrorPopup={openErrorPopup}
        errorMessage={errorMessage}
        headerMessage={headerMessage}
        componentName={componentName}
        onClose={() => setOpenErrorPopup(false)}
      />
    </>
  );
}
